import { useAuth } from 'react-oidc-context';
import {
  GetSearchSuggestions,
  getUserSearchHistory
} from 'api/services/SearchService';
import { Segments, SegmentsFoodOnly } from 'api/types/ReferenceData';
import SeparatorLine from 'components/common/SeparatorLine';
import { extractSuggestionValues } from 'helpers/Suggestions';
import { useIsFoodSegmentAvailable } from 'hooks/useIsFoodSegmentAvailable';
import useSupplierPortalUser from 'hooks/useSupplierPortalUser';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import Autosuggest, {
  ChangeEvent,
  SuggestionSelectedEventData
} from 'react-autosuggest';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { legacyBreakpoints } from 'theme/legacyThemeVariables';
import { tss } from 'tss-react/mui';
import { ProductType } from 'types/Products';
import { SearchSuggestionsRequest, Suggestions } from 'types/Suggestions';
import { FetchResponse, SegmentTag } from 'types/global';
import {
  IS_UXBEAUTY,
  WHITELABEL_EVENT_KEY
} from 'components/constants/Constants';
import {
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { InputGroup } from 'react-bootstrap';
import dayjs from 'dayjs';
import { SearchTarget, SUPPLIER_SEARCH_TARGET } from 'types/Search';

interface SearchBarProps {
  target: SearchTarget;
  size?: number;
  onSubmit: (event: any, currentValue: string) => void;
  placeholder: string;
  segment: SegmentTag;
  onSegmentChange: (currentValue: SegmentTag) => void;
  bookmarksEnabled: boolean;
  onToggleBookmarks: () => void;
  defaultSearchValue: string;
}

interface SearchHistoryProps {
  query: string;
  date: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  target,
  size = 10,
  onSubmit,
  placeholder,
  segment,
  onSegmentChange,
  bookmarksEnabled,
  onToggleBookmarks,
  defaultSearchValue
}: SearchBarProps) => {
  const { classes, cx } = useStyles({
    bookmarksActive: bookmarksEnabled
  });
  const { event: eventKey } = useParams();
  const { hasSearchViewPermission: isBrandPortalMember } =
    useSupplierPortalUser(eventKey);

  const isBrandPortal: boolean = isBrandPortalMember && eventKey !== undefined;
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [currentValue, setCurrentValue] = useState<string>(defaultSearchValue);
  const searchInput = useRef<Autosuggest | null>(null);
  const [searchHistory, setSearchHistory] = useState<HistoryEntry[]>();
  const [canShowHistory, setCanShowHistory] = useState<boolean>(false);
  const hasFoodPermissions = useIsFoodSegmentAvailable();

  const { isAuthenticated } = useAuth();

  const handleCloseHistory = useCallback(() => setCanShowHistory(false), []);
  const historyRef = useOutsideClick<HTMLDivElement>(handleCloseHistory);
  interface HistoryItem {
    query: string;
    time: string;
    date: string;
  }
  type HistoryEntry = {
    date: string;
    queries: HistoryItem[];
  };
  const iteratableSearchHistory = (data: SearchHistoryProps[]) => {
    const historyEntries: HistoryEntry[] = [];

    // exampleSearchHistory.forEach((entry: SearchHistoryProps) => {
    data.forEach((entry: SearchHistoryProps) => {
      const items: any = {
        query: entry.query,
        time: dayjs(entry.date).format('HH:mm'),
        date: dayjs(entry.date).format('YYYY-MM-DD')
      };

      const index = historyEntries.findIndex(
        entry => entry.date === items.date
      );

      if (index === -1) {
        historyEntries.push({ date: items.date, queries: [items] }); // push new entry
      } else {
        historyEntries[index].queries.push(items); // push into existing index
      }
    });
    setSearchHistory(historyEntries);
    setCanShowHistory(true);
  };

  useEffect(() => {
    // gets triggered on searchbar clear
    if (!currentValue.length) {
      searchInput?.current?.input?.focus();
    }
  }, [currentValue]);
  const getSuggestions = (currentValue: string) => {
    if (target === SUPPLIER_SEARCH_TARGET) return;

    const searchSuggestions: SearchSuggestionsRequest = {
      query: currentValue,
      size: size,
      segment: segment,
      target: target,
      event: WHITELABEL_EVENT_KEY
    };

    const handleSuccess = (response: FetchResponse) => {
      const suggestionsData: Suggestions = response?.data;
      const suggestionValues = extractSuggestionValues(suggestionsData);
      setSuggestions(suggestionValues);
    };

    const handleError = () => {
      setSuggestions([]);
    };

    GetSearchSuggestions(searchSuggestions, handleSuccess, handleError);
  };

  const onChange = (_event: FormEvent, changeEvent: ChangeEvent) => {
    setCurrentValue(changeEvent.newValue);
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    getSuggestions(value);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const renderSuggestion = (suggestion: string) => {
    return (
      <span style={{ fontFamily: theme.typography.fontFamily }}>
        {suggestion}
      </span>
    );
  };

  const onSuggestionSelected = (
    event: FormEvent,
    { suggestionValue }: SuggestionSelectedEventData<string>
  ) => {
    onSubmit(event, suggestionValue);
  };

  const onSearchBarClear = (event: FormEvent) => {
    onSubmit(event, '');
    setCurrentValue('');
  };

  const showHistory = () => {
    getUserSearchHistory(
      target,
      (res: any) => {
        iteratableSearchHistory(res.data);
      },
      () => toast.error('Could not load search history, try again later')
    );
  };

  const autosuggestProps = {
    placeholder: placeholder,
    value: currentValue,
    onChange: onChange,
    type: 'search',
    inputProps: {
      inputMode: 'search',
      enterKeyHint: 'search'
    }
  };

  const segmentOptions =
    target === SUPPLIER_SEARCH_TARGET ||
    (hasFoodPermissions && target === ProductType.INGREDIENTS && !isBrandPortal)
      ? [...Segments, ...SegmentsFoodOnly]
      : Segments;
  const theme = useTheme();

  return (
    <form
      className={cx('search-bar', classes.searchBar)}
      onSubmit={event => {
        onSubmit(event, currentValue);
      }}
    >
      {!IS_UXBEAUTY && (
        <div>
          <TextField
            variant="standard"
            fullWidth
            select
            value={segmentOptions.find(o => o.value === segment)?.value}
            onChange={e => onSegmentChange(e.target.value as SegmentTag)}
          >
            {segmentOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      )}

      <InputGroup>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={suggestion => suggestion}
          renderSuggestion={renderSuggestion}
          onSuggestionSelected={onSuggestionSelected}
          inputProps={autosuggestProps}
          shouldRenderSuggestions={value => value.trim().length > 2}
          ref={searchInput}
          renderInputComponent={inputProps => (
            <TextField
              {...inputProps}
              color="primary"
              size="medium"
              type="search"
              variant="standard"
              fullWidth
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onSubmit(event, currentValue);
                  onSuggestionsClearRequested();
                }
              }}
              slotProps={{
                htmlInput: {
                  enterKeyHint: 'search'
                },
                input: {
                  inputMode: 'search',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={event => {
                          currentValue === ''
                            ? onSubmit(event, currentValue)
                            : onSearchBarClear(event);
                        }}
                        aria-label={
                          currentValue === '' ? 'Search' : 'Clear search'
                        }
                      >
                        {currentValue === '' ? <SearchIcon /> : <ClearIcon />}
                      </IconButton>
                      {!isAuthenticated ? (
                        <Tooltip title="Please log in to use your personal search history.">
                          <span>
                            <IconButton
                              color="primary"
                              size="small"
                              disabled
                              aria-label="Please log in to use your personal search history."
                            >
                              <HistoryIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Search history">
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={showHistory}
                                aria-label="Search history"
                                disabled={target === SUPPLIER_SEARCH_TARGET}
                              >
                                <HistoryIcon />
                                {canShowHistory && (
                                  <div
                                    ref={historyRef}
                                    className="search-history-results"
                                    style={{
                                      boxShadow: '0 0 12px rgb(0 0 0 / 16%)'
                                    }}
                                  >
                                    <p
                                      className=" m-0 mb-n3 p-2 fw-bolder"
                                      style={{
                                        fontSize: '20px',
                                        color: '#343434'
                                      }}
                                    >
                                      Search history
                                    </p>
                                    <SeparatorLine color={'#c9c9c9'} />
                                    {searchHistory &&
                                      searchHistory.map(
                                        (
                                          historyResult: HistoryEntry,
                                          index: number
                                        ) => {
                                          return (
                                            <div key={index}>
                                              <p
                                                style={{
                                                  fontSize: '18px',
                                                  color: '#343434'
                                                }}
                                                className="fw-bolder m-0 mb-0  px-2"
                                              >
                                                {historyResult.date}
                                              </p>

                                              <div>
                                                {historyResult.queries.map(
                                                  (
                                                    query: HistoryItem,
                                                    key: number
                                                  ) => {
                                                    return (
                                                      <div
                                                        key={key}
                                                        className="d-flex gap-2 px-2 history-item"
                                                        onClick={event => {
                                                          onSubmit(
                                                            event,
                                                            query.query
                                                          );
                                                          setCanShowHistory(
                                                            false
                                                          );
                                                          setCurrentValue(
                                                            query.query
                                                          );
                                                        }}
                                                      >
                                                        <p
                                                          className="my-1"
                                                          style={{
                                                            fontSize: '14px'
                                                          }}
                                                        >
                                                          {' '}
                                                          {query.time}
                                                        </p>
                                                        <p
                                                          className=" my-1 fw-500 "
                                                          style={{
                                                            color: '#343434',
                                                            fontSize: '14px'
                                                          }}
                                                        >
                                                          {query.query}
                                                        </p>
                                                      </div>
                                                    );
                                                  }
                                                )}
                                                <SeparatorLine
                                                  color={'#c9c9c9'}
                                                />
                                              </div>
                                            </div>
                                          );
                                        }
                                      )}
                                  </div>
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              target === 'SUPPLIERS'
                                ? 'Coming soon'
                                : 'Your bookmarks'
                            }
                          >
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={onToggleBookmarks}
                                aria-label="Bookmarks"
                                disabled={target === 'SUPPLIERS'}
                              >
                                {bookmarksEnabled ? (
                                  <StarIcon />
                                ) : (
                                  <StarOutlineIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </InputAdornment>
                  )
                }
              }}
            />
          )}
        />

        <input type={'submit'} hidden />
      </InputGroup>
    </form>
  );
};

const useStyles = tss
  .withParams<{ bookmarksActive: boolean }>()
  .create(({ theme }) => ({
    searchBar: {
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
      flexGrow: 1,
      gap: 16,

      [legacyBreakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'normal'
      },

      '& .react-select__menu-list': {
        '& .react-select__option--is-focused, .react-select__option--is-selected':
          {
            backgroundColor: `${theme.palette.primary.main} !important`
          }
      },
      '& .react-select__control.react-select__control--is-focused': {
        boxShadow: `var(--falcon-box-shadow-inset),0 0 0 .25rem ${theme.palette.primary.main}40 !important`
      }
    }
  }));

export default SearchBar;
